/**
 * Server state for My Dashboard (HS-1438).
 *
 * **Every read and every write in this file now reaches the Admin API.** HS-1481 moved the four
 * reads and the notification writes; HS-1517 moved the last two, the access read and the
 * extension request, and this file no longer names the mock transport at all. Nothing else in the
 * feature moved with them — not the adapter, not a component, not a test of either. That was the
 * point of confining the transport to one file.
 *
 * What did not move is `DELETE …/access/extension-request`, because it does not exist: see the
 * block where `cancelExtensionMutation` used to be, below.
 *
 * The mappers exist because the generated DTOs and ours disagree about absence. Note that the API
 * sends an explicit `null` where the schema only says "optional", so every check here treats null
 * and undefined alike — reading one as present is how the header hung on its first live run.
 *
 * Independent reads rather than one aggregate, per the contract: they cache and invalidate
 * separately, so ending a session refreshes the header and the summary without re-fetching files,
 * and one slow card cannot hold up the page. The two list cards are not dashboard reads at all:
 * recent sessions is `/sessions` page 1 and files is `/files` page 1, each with a page size of 5,
 * so the cards share the lists' cache entries and their adapters.
 *
 * Mutations are hand-built rather than taken from `@smartsash/server-ops`: that package composes
 * *generated* mutation hooks with the invalidation registry, and each mutation here maps the
 * generated DTO into ours before the adapter sees it, which a composed hook has no seam for. The
 * invalidation each one performs is written out here instead.
 */
import {
  getApiPortalMeAccess,
  getApiPortalMeLabSessionActive,
  getApiPortalMeNotifications,
  getApiPortalMeSessionsSummary,
  postApiPortalMeAccessExtensionRequest,
  postApiPortalMeLabSessionSessionIdEnd,
  postApiPortalMeLabSessionSessionIdResume,
  postApiPortalMeNotificationsIdAcknowledge,
  postApiPortalMeNotificationsIdDismiss,
  type PortalAccessDto,
  type PortalActiveLabSessionDto,
  type PortalNotificationFeedDto,
  type PortalSummaryDto,
} from '@smartsash/api-client';
import { keepPreviousData, queryOptions, type QueryClient } from '@tanstack/react-query';

import {
  toAccessView,
  toDashboardSessionView,
  toNotificationsView,
  toPeriodSummaryView,
} from './dashboard.adapter';
import type {
  AccessDto,
  AccessView,
  ActiveLabSessionResponseDto,
  DashboardSessionView,
  NotificationsDto,
  NotificationsView,
  PeriodDto,
  PeriodSummaryView,
  SessionSummaryDto,
} from './dashboard.types';

import { filesKeys, filesQuery } from '@/features/files/files.api';
import { serviceRequestKeys } from '@/features/service-requests/serviceRequestKeys';
import { sessionsQuery } from '@/features/sessions/sessions.api';

/** How many rows the recent-sessions preview shows. The design's card is a fixed short list. */
export const RECENT_SESSIONS_PAGE_SIZE = 5;
/** How many rows the files preview shows. */
export const RECENT_FILES_PAGE_SIZE = 5;

/**
 * Cache keys, kept in one place so the eventual swap to the generated
 * `getGetApiPortal…QueryKey()` helpers is a single edit and no caller has hand-written a key.
 *
 * Every dashboard-owned key is rooted at `['portal', 'dashboard']` so a single `invalidateQueries`
 * refreshes those cards after a write that changes more than one. The two list cards live under
 * the lists' own roots (`['portal', 'sessions']`, `['portal', 'files']`) and are invalidated by
 * name where a write touches them — see `endSessionMutation`.
 */
export const dashboardKeys = {
  root: ['portal', 'dashboard'] as const,
  activeSession: () => ['portal', 'dashboard', 'activeSession'] as const,
  /** Prefix of every period's summary — what a `SashIQEvent` invalidates (My Sash IQ shares it). */
  summaryRoot: ['portal', 'dashboard', 'summary'] as const,
  summary: (period: PeriodDto) => ['portal', 'dashboard', 'summary', period] as const,
  access: () => ['portal', 'dashboard', 'access'] as const,
  notifications: () => ['portal', 'dashboard', 'notifications'] as const,
};

/**
 * `session` may be absent *or* explicitly null when nothing is running, and `livePreview` is null
 * on every profile that publishes no stream. Both absences collapse to null here.
 */
function toActiveSessionDto(dto: PortalActiveLabSessionDto): ActiveLabSessionResponseDto {
  // `?? null` is what collapses the two absences. The generated type claims these fields are
  // merely optional, so a bare `=== null` reads as impossible to the type checker even though the
  // API sends exactly that; coalescing first keeps the check both honest and type-correct.
  const session = dto.session ?? null;
  const livePreview = session?.livePreview ?? null;

  return {
    serverTime: dto.serverTime,
    session:
      session === null
        ? null
        : {
            sessionId: text(session.sessionId, 'labSession.sessionId'),
            deviceId: text(session.deviceId, 'labSession.deviceId'),
            deviceName: session.deviceName ?? null,
            labId: session.labId ?? null,
            labName: session.labName ?? null,
            institutionId: session.institutionId,
            startedAt: session.startedAt,
            endsAt: session.endsAt ?? null,
            presence: session.presence,
            sashState: session.sashState,
            sashOpenSince: session.sashOpenSince ?? null,
            livePreview:
              livePreview === null
                ? null
                : {
                    streamId: text(livePreview.streamId, 'livePreview.streamId'),
                    thumbnailUrl: livePreview.thumbnailUrl ?? null,
                    capturedAt: livePreview.capturedAt ?? null,
                    canJoin: livePreview.canJoin,
                  },
          },
  };
}

/**
 * The header's read. Also the only place the portal learns a kiosk session id, which is the scope
 * key a device-channel subscription needs.
 */
export function activeSessionQuery() {
  return queryOptions<DashboardSessionView>({
    queryKey: dashboardKeys.activeSession(),
    queryFn: async ({ signal }) =>
      toDashboardSessionView(
        toActiveSessionDto(await getApiPortalMeLabSessionActive(undefined, signal)),
      ),
    // The timer re-renders from timestamps every second; re-reading the session that often would
    // be pointless traffic. Events (contract §4) are what make this fresh, and until they exist a
    // refetch on window focus is the honest compromise.
    staleTime: 30_000,
  });
}

/** One metric, three independently nullable numbers. Uniform across all four metrics. */
function toMetricDto(metric: {
  value?: number | null;
  previous?: number | null;
  delta?: number | null;
}) {
  return {
    value: metric.value ?? null,
    previous: metric.previous ?? null,
    delta: metric.delta ?? null,
  };
}

/**
 * `period` is echoed so a bug in the backend's range maths is visible rather than inferred. That
 * only works if the echo is checked, so a value other than the one we asked for throws here
 * instead of being quietly rendered under the wrong label.
 */
function toSummaryDto(dto: PortalSummaryDto, requested: PeriodDto): SessionSummaryDto {
  const echoed = text(dto.period, 'summary.period');
  if (echoed !== requested) {
    throw new Error(`Admin API answered period "${echoed}" for a "${requested}" request`);
  }

  return {
    period: requested,
    from: dto.from,
    to: dto.to,
    metrics: {
      sessions: toMetricDto(dto.metrics.sessions),
      cleanRate: toMetricDto(dto.metrics.cleanRate),
      avgDuration: toMetricDto(dto.metrics.avgDuration),
      totalTime: toMetricDto(dto.metrics.totalTime),
    },
    sessionsCompleted: dto.sessionsCompleted,
    sessionsRequired: dto.sessionsRequired,
  };
}

export function summaryQuery(period: PeriodDto) {
  return queryOptions<PeriodSummaryView>({
    queryKey: dashboardKeys.summary(period),
    queryFn: async ({ signal }) =>
      toPeriodSummaryView(
        toSummaryDto(await getApiPortalMeSessionsSummary({ period }, undefined, signal), period),
      ),
    // Switching the range keeps the previous period's numbers on screen — dimmed, each with its
    // own spinner — instead of tearing the panel down and rebuilding it. Without this the card
    // would unmount its grid on every switch, which reads as the whole block flashing.
    placeholderData: keepPreviousData,
  });
}

/** Shares the My Sessions contract (HS-1440) — the dashboard is that read with a page size of 5. */
export function recentSessionsQuery() {
  return sessionsQuery({ page: 1, pageSize: RECENT_SESSIONS_PAGE_SIZE });
}

/** Shares the My Files contract (HS-1441) — the dashboard is that read with a page size of 5. */
export function recentFilesQuery() {
  return filesQuery({ page: 1, pageSize: RECENT_FILES_PAGE_SIZE });
}

/* ------------------------------------------------------------------ *
 * Generated client → our DTOs
 * ------------------------------------------------------------------ */

/**
 * The API's schemas mark **every** string `nullable: true` — a document-wide setting the backend
 * scoped out of its `required` fix, so an identifier the contract guarantees still generates as
 * `string | null`. A null there is a contract violation, not a value, and naming the field beats
 * an empty chip three layers down.
 */
function text(value: string | null, field: string): string {
  if (value === null) {
    throw new Error(`Admin API sent null for "${field}", which the contract guarantees`);
  }
  return value;
}

/**
 * The generated DTO and ours carry the same members under the same names, so this is a
 * nullability normaliser rather than a translation: the schema marks `expiresAt`,
 * `suspendedReason` and `requestedAt` merely optional, while the API sends an explicit `null`.
 * Both absences collapse here, the way `toActiveSessionDto` collapses the session's.
 *
 * `state`, `canRequestExtension` and `extensionRequest.status` are `required` in the schema, so
 * they arrive as values and need no defaulting. A missing `extensionRequest` would be a contract
 * violation, not an absence, and is left to fail loudly.
 */
function toAccessDto(dto: PortalAccessDto): AccessDto {
  return {
    state: dto.state,
    expiresAt: dto.expiresAt ?? null,
    suspendedReason: dto.suspendedReason ?? null,
    canRequestExtension: dto.canRequestExtension,
    extensionRequest: {
      status: dto.extensionRequest.status,
      requestedAt: dto.extensionRequest.requestedAt ?? null,
    },
  };
}

/**
 * Read in the route loader: a suspended user renders the full-page state and no card. The card
 * reads are still prefetched alongside it, because the loader does not wait for this one first —
 * see `dashboard.contract.md` §3.6.
 *
 * No `staleTime`. A suspension has to show up on the next navigation rather than after a window —
 * the header's 30 s is a timer optimisation and does not generalise to this.
 */
export function accessQuery() {
  return queryOptions<AccessView>({
    queryKey: dashboardKeys.access(),
    queryFn: async ({ signal }) =>
      toAccessView(toAccessDto(await getApiPortalMeAccess(undefined, signal))),
  });
}

/**
 * The whole feed, not the unread slice: the portal filters acknowledged and dismissed items
 * itself, so `unreadOnly` is deliberately not sent and the server's default answers everything.
 */
function toNotificationsDto(dto: PortalNotificationFeedDto): NotificationsDto {
  return {
    totalCount: dto.totalCount,
    items: (dto.items ?? []).map((item) => {
      const document = item.document ?? null;

      return {
        id: text(item.id, 'notification.id'),
        title: text(item.title, 'notification.title'),
        body: text(item.body, 'notification.body'),
        severity: item.severity,
        delivery: item.delivery,
        createdAt: item.createdAt,
        requiresAcknowledgement: item.requiresAcknowledgement,
        acknowledgedAt: item.acknowledgedAt ?? null,
        dismissedAt: item.dismissedAt ?? null,
        document:
          document === null
            ? null
            : {
                id: text(document.id, 'notification.document.id'),
                name: text(document.name, 'notification.document.name'),
                url: text(document.url, 'notification.document.url'),
              },
      };
    }),
  };
}

export function notificationsQuery() {
  return queryOptions<NotificationsView>({
    queryKey: dashboardKeys.notifications(),
    queryFn: async ({ signal }) =>
      toNotificationsView(
        toNotificationsDto(await getApiPortalMeNotifications(undefined, undefined, signal)),
      ),
  });
}

/* ------------------------------------------------------------------ *
 * Mutations
 * ------------------------------------------------------------------ */

/** HTTP status the contract defines as "already in the state you asked for". */
const STATUS_CONFLICT = 409;

interface HttpLikeError {
  status?: number;
}

/**
 * A 409 means the session was already ended — by the kiosk, by a timeout, or by a duplicate
 * click. The user's intent is satisfied either way, so the screen must not apologise for a race
 * it won; it refreshes and shows the ended state.
 */
export function isAlreadyApplied(error: unknown): boolean {
  return (error as HttpLikeError | null)?.status === STATUS_CONFLICT;
}

/**
 * Run a write, and treat "already in the state you asked for" as success.
 *
 * The re-read is what makes that honest rather than a swallowed error: the user's intent is
 * satisfied either way, but only the server knows what the resulting state actually is — a
 * session someone else ended, an extension request raised from another tab. Returning the truth
 * beats returning the optimistic guess the caller had in mind.
 */
async function orAlreadyApplied<T>(write: () => Promise<T>, reread: () => Promise<T>): Promise<T> {
  try {
    return await write();
  } catch (error) {
    if (!isAlreadyApplied(error)) throw error;
    return await reread();
  }
}

export interface EndSessionInput {
  sessionId: string;
  /**
   * Records that the user was shown the guarded dialog and chose to end anyway. Evidence, not
   * instruction: the backend still decides `endReason` from the sash state it observed.
   */
  acknowledgedSashOpen?: boolean;
}

export function endSessionMutation(queryClient: QueryClient) {
  return {
    /**
     * The answer is the §3.1 envelope with a **null session**, not the ended session — the
     * header re-renders into its idle state from a shape it already understands. A repeat
     * answers 409 `SESSION_ALREADY_ENDED`, which is success: the session is ended either way.
     */
    mutationFn: async (input: EndSessionInput) =>
      toDashboardSessionView(
        toActiveSessionDto(
          await orAlreadyApplied(
            () =>
              postApiPortalMeLabSessionSessionIdEnd(input.sessionId, {
                acknowledgedSashOpen: input.acknowledgedSashOpen ?? false,
              }),
            () => getApiPortalMeLabSessionActive(),
          ),
        ),
      ),
    onSettled: async () => {
      // The header and the period metrics both change when a session ends; recent sessions gains
      // a row, and so may files — the session's recording and report are files with that session
      // as their origin. The two list cards share the lists' cache roots, so those are named.
      await queryClient.invalidateQueries({ queryKey: dashboardKeys.root });
      await queryClient.invalidateQueries({ queryKey: ['portal', 'sessions'] });
      await queryClient.invalidateQueries({ queryKey: filesKeys.root });
    },
  };
}

export function resumeSessionMutation(queryClient: QueryClient) {
  return {
    mutationFn: async (input: { sessionId: string }) =>
      toDashboardSessionView(
        toActiveSessionDto(await postApiPortalMeLabSessionSessionIdResume(input.sessionId)),
      ),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: dashboardKeys.activeSession() });
    },
  };
}

export function requestExtensionMutation(queryClient: QueryClient) {
  return {
    /**
     * `POST /api/portal/me/access/extension-request` (contract §3.6).
     *
     * **201 answers the whole access document**, not the new `extensionRequest` on its own — the
     * contract document says otherwise and is wrong; `PortalAccessController` says so in its own
     * header, and the generated type agrees. So the answer feeds the same adapter as the read.
     * It is not written into the cache, though: `onSettled` invalidates the access key and the
     * card redraws from the refetch that follows.
     *
     * **409 carries no `data`** — `{ success: false, errorCode: "REQUEST_ALREADY_PENDING" }` — so
     * unlike the session writes it cannot be used as the new state; `orAlreadyApplied` re-reads,
     * which is what makes "the intent is already satisfied" an honest claim rather than an
     * optimistic guess. A request raised from another tab lands here.
     *
     * The message is not sent: no surface collects one, and the 400 the backend answers a
     * message over 500 characters is therefore unreachable.
     */
    mutationFn: async (input: { message?: string }) =>
      toAccessView(
        toAccessDto(
          await orAlreadyApplied(
            () => postApiPortalMeAccessExtensionRequest(input),
            () => getApiPortalMeAccess(),
          ),
        ),
      ),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: dashboardKeys.access() });
      // The banner's request is an AccessExtension service request (HS-1548, Decision 10), so
      // My requests must show it without a reload.
      await queryClient.invalidateQueries({ queryKey: serviceRequestKeys.root });
    },
  };
}

/*
 * `cancelExtensionMutation` was here until HS-1517.
 *
 * It called `DELETE /api/portal/me/access/extension-request`, which the Admin API has never
 * served: HS-1442 wrote the route into the profile contract (§4) and built the portal half
 * against the mock, and the backend half was never ordered until HS-1518. With the read and the
 * request now served, a mock cancel would have looked at module state the other two no longer
 * write, answered 404, and been read as "nothing to cancel" — a button that resolves successfully
 * and changes nothing. `describeAccess` therefore draws no button on a pending request, and this
 * mutation comes back with the endpoint.
 */

export function acknowledgeNotificationMutation(queryClient: QueryClient) {
  return {
    mutationFn: async (input: { id: string }) =>
      toNotificationsView(
        toNotificationsDto(await postApiPortalMeNotificationsIdAcknowledge(input.id)),
      ),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: dashboardKeys.notifications() });
    },
  };
}

/**
 * Dismissal is a server write, not `localStorage`: a toast dismissed here must not reappear on
 * another device, and the message itself stays in My Notifications.
 */
export function dismissNotificationMutation(queryClient: QueryClient) {
  return {
    mutationFn: async (input: { id: string }) =>
      toNotificationsView(
        toNotificationsDto(await postApiPortalMeNotificationsIdDismiss(input.id)),
      ),
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: dashboardKeys.notifications() });
    },
  };
}
